/**
 * Trunnion AI - Execution Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import { executionsApi } from '@/api/executions.api';
import { useNotificationStore } from '@/stores/notificationStore';
import type { ExecutionFilters } from '@/types';

export function useExecutionList(filters: ExecutionFilters = {}) {
  return useQuery({
    queryKey: queryKeys.executions.list(filters as Record<string, unknown>),
    queryFn: () => executionsApi.list(filters),
    refetchInterval: 5_000, // Poll every 5s for active executions
  });
}

export function useExecution(id: string) {
  return useQuery({
    queryKey: queryKeys.executions.detail(id),
    queryFn: () => executionsApi.getById(id),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'running' || status === 'waiting_approval') return 3_000;
      return false;
    },
  });
}

export function useCancelExecution() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      executionsApi.cancel(id, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.executions.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.executions.detail(variables.id),
      });
      addNotification({
        type: 'info',
        title: 'Execution Cancelled',
        message: 'The execution has been cancelled.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Cancel Failed',
        message: error.message,
      });
    },
  });
}

export function useApproveStep() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({
      executionId,
      stepId,
      justification,
    }: {
      executionId: string;
      stepId: string;
      justification: string;
    }) => executionsApi.approve(executionId, stepId, justification),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.executions.detail(variables.executionId),
      });
      addNotification({
        type: 'success',
        title: 'Step Approved',
        message: 'The step has been approved and execution will continue.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Approval Failed',
        message: error.message,
      });
    },
  });
}

export function useDenyStep() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({
      executionId,
      stepId,
      reason,
    }: {
      executionId: string;
      stepId: string;
      reason: string;
    }) => executionsApi.deny(executionId, stepId, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.executions.detail(variables.executionId),
      });
      addNotification({
        type: 'info',
        title: 'Step Denied',
        message: 'The step has been denied.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Denial Failed',
        message: error.message,
      });
    },
  });
}
